const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const getProgressPercent = (processed, total) => {
  const safeTotal = toNumber(total);
  const safeProcessed = toNumber(processed);
  if (safeTotal <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((safeProcessed / safeTotal) * 100)));
};

const normalizeErrors = (errors) => {
  if (!Array.isArray(errors)) return [];
  return errors.filter((item) => typeof item === "string" && item.trim() !== "");
};

const parseCodeFromError = (errorText) => {
  if (typeof errorText !== "string") return "";
  const match = errorText.match(/ProductPartCode\/ItemCode\s+(.+?)(?:\.|$)/i);
  return match?.[1]?.trim() || "";
};

const createMetricLine = (summary, label) => {
  const parts = [];
  const processed =
    summary.totalRows > 0
      ? summary.totalRows
      : summary.importedRows + summary.updatedRows + summary.skippedRows;
  parts.push(`${processed} processed`);
  if (summary.importedRows > 0) parts.push(`${summary.importedRows} imported`);
  if (summary.updatedRows > 0) parts.push(`${summary.updatedRows} updated`);
  if (summary.skippedRows > 0) parts.push(`${summary.skippedRows} skipped`);
  return `${label}: ${parts.join(" • ")}`;
};

const buildIssueGroup = (label, errors) => {
  const codeIssues = [];
  const textIssues = [];

  errors.forEach((message) => {
    const code = parseCodeFromError(message);
    if (code) {
      codeIssues.push(code);
    } else {
      textIssues.push(message);
    }
  });

  const uniqueCodes = [...new Set(codeIssues)];
  const uniqueTexts = [...new Set(textIssues)];

  return {
    label,
    description:
      uniqueCodes.length > 0 ? "Product with item code not found" : "",
    codes: uniqueCodes,
    texts: uniqueTexts,
  };
};

const buildStatusLine = (label, summary, status) => {
  const parts = [];
  const processed = toNumber(summary.processedRows);
  const totalRows = toNumber(summary.totalRows);
  const statusLabel = status ? ` (${status.toLowerCase()})` : "";

  if (totalRows > 0) {
    parts.push(`${processed}/${totalRows} processed`);
  } else {
    parts.push(`${processed} processed`);
  }
  if (toNumber(summary.importedRows) > 0) parts.push(`${toNumber(summary.importedRows)} imported`);
  if (toNumber(summary.updatedRows) > 0) parts.push(`${toNumber(summary.updatedRows)} updated`);
  if (toNumber(summary.skippedRows) > 0) parts.push(`${toNumber(summary.skippedRows)} skipped`);
  if (toNumber(summary.failedRows) > 0) parts.push(`${toNumber(summary.failedRows)} failed`);

  return `${label}${statusLabel}: ${parts.join(" • ")}`;
};

const normalizeProductSummary = (payload) => {
  const data = payload?.productResponse?.body?.data || {};
  return {
    totalRows: toNumber(data.totalRows),
    importedRows: toNumber(data.importedRows),
    updatedRows: toNumber(data.updatedRows),
    skippedRows: toNumber(data.skippedRows),
    errors: normalizeErrors(data.errors),
  };
};

const normalizeSupplierSummary = (payload) => {
  const data = payload?.supplierResponse?.body?.data || {};
  const summary = data.summary || {};
  return {
    totalRows: toNumber(summary.totalRows),
    importedRows: toNumber(summary.importedRows),
    updatedRows: toNumber(summary.updatedRows),
    skippedRows: toNumber(summary.skippedRows),
    errors: normalizeErrors(summary.errors),
  };
};

export const formatProductCatalogueImportResult = (payload) => {
  const product = normalizeProductSummary(payload);
  const supplier = normalizeSupplierSummary(payload);
  const totalRows = product.totalRows + supplier.totalRows;
  const processedRows =
    product.importedRows +
    product.updatedRows +
    product.skippedRows +
    supplier.importedRows +
    supplier.updatedRows +
    supplier.skippedRows;

  const productIssueGroup = buildIssueGroup("Product Issues", product.errors);
  const supplierIssueGroup = buildIssueGroup("Supplier Issues", supplier.errors);

  const hasWarnings =
    productIssueGroup.codes.length > 0 ||
    productIssueGroup.texts.length > 0 ||
    supplierIssueGroup.codes.length > 0 ||
    supplierIssueGroup.texts.length > 0;

  const lines = [createMetricLine(product, "Product"), createMetricLine(supplier, "Supplier")];

  const groupedIssues = [];
  if (productIssueGroup.codes.length > 0 || productIssueGroup.texts.length > 0) {
    groupedIssues.push(productIssueGroup);
  }
  if (supplierIssueGroup.codes.length > 0 || supplierIssueGroup.texts.length > 0) {
    groupedIssues.push(supplierIssueGroup);
  }

  return {
    title: hasWarnings
      ? "Import completed with warnings"
      : "Import completed successfully",
    lines,
    hasWarnings,
    issueCount:
      productIssueGroup.codes.length +
      productIssueGroup.texts.length +
      supplierIssueGroup.codes.length +
      supplierIssueGroup.texts.length,
    groupedIssues,
    progressPercent: getProgressPercent(processedRows, totalRows),
  };
};

export const formatImportJobStatusNotificationResult = (statusMap) => {
  const terminalStatuses = ["completed", "failed", "cancelled"];
  const productData = statusMap?.product || null;
  const supplierData = statusMap?.supplier || null;

  const productSummary = {
    totalRows: toNumber(productData?.totalRows),
    processedRows: toNumber(productData?.processedRows),
    importedRows: toNumber(productData?.importedRows),
    updatedRows: toNumber(productData?.updatedRows),
    skippedRows: toNumber(productData?.skippedRows),
    failedRows: toNumber(productData?.failedRows),
  };
  const supplierSummary = {
    totalRows: toNumber(supplierData?.totalRows),
    processedRows: toNumber(supplierData?.processedRows),
    importedRows: toNumber(supplierData?.importedRows),
    updatedRows: toNumber(supplierData?.updatedRows),
    skippedRows: toNumber(supplierData?.skippedRows),
    failedRows: toNumber(supplierData?.failedRows),
  };
  const totalRows = productSummary.totalRows + supplierSummary.totalRows;
  const processedRows = productSummary.processedRows + supplierSummary.processedRows;

  const productErrors = normalizeErrors(
    (productData?.errors || []).map((item) => item?.errorMessage || item),
  );
  const supplierErrors = normalizeErrors(
    (supplierData?.errors || []).map((item) => item?.errorMessage || item),
  );

  const productIssueGroup = buildIssueGroup("Product Issues", productErrors);
  const supplierIssueGroup = buildIssueGroup("Supplier Issues", supplierErrors);

  const statuses = [productData?.status, supplierData?.status].filter(Boolean);
  const isInProgress =
    statuses.length === 0 || statuses.some((status) => !terminalStatuses.includes(String(status).toLowerCase()));
  const hasWarnings =
    productIssueGroup.codes.length > 0 ||
    productIssueGroup.texts.length > 0 ||
    supplierIssueGroup.codes.length > 0 ||
    supplierIssueGroup.texts.length > 0;

  const lines = [
    buildStatusLine("Product", productSummary, productData?.status),
    buildStatusLine("Supplier", supplierSummary, supplierData?.status),
  ];

  const groupedIssues = [];
  if (productIssueGroup.codes.length > 0 || productIssueGroup.texts.length > 0) {
    groupedIssues.push(productIssueGroup);
  }
  if (supplierIssueGroup.codes.length > 0 || supplierIssueGroup.texts.length > 0) {
    groupedIssues.push(supplierIssueGroup);
  }

  return {
    title: isInProgress
      ? "Import in progress"
      : hasWarnings
        ? "Import completed with warnings"
        : "Import completed successfully",
    lines,
    hasWarnings,
    issueCount:
      productIssueGroup.codes.length +
      productIssueGroup.texts.length +
      supplierIssueGroup.codes.length +
      supplierIssueGroup.texts.length,
    groupedIssues,
    progressPercent: getProgressPercent(processedRows, totalRows),
    autoClose: isInProgress ? false : hasWarnings ? 8000 : 5000,
  };
};
